import React from "react";
import { MDBCard, MDBCardBody, MDBCol, MDBRow } from "mdbreact";
import Skeleton from "react-loading-skeleton";
import { useFlexPackages } from "./FlexBenefitsPlanCard";
import {
  flexBenefitsAdministratorName,
  flexBenefitsAdministratorText,
  flexBenefitsBillingLabel,
  flexBenefitsBillingPhone,
  flexBenefitsBillingSubtext,
  flexBenefitsClaimsEmail,
  flexBenefitsClaimsLabel,
  flexBenefitsClaimsPhone,
  flexBenefitsSupportTitle,
} from "../../core/constants/strings";
import { helpEmailUrl, helpPhoneUrl } from "../../core/constants/endpoints";
import FlexBenefitsTpaLinks from "./FlexBenefitsTpaLinks";

/**
 * Static TPA contact block for members holding a FlexBenefits package.
 *
 * Simple component: one card, one concern. Analogous to HelpWidget rather than
 * AflacDisclaimer — there is no interactive state, only visibility early returns.
 *
 * Contact values are constants because they are per product line, not per member.
 * They come from the FlexBenefits member-services page; see the note in strings.ts
 * about the administrator attribution still needing confirmation.
 */
const FlexBenefitsSupport = () => {
  const { isLoading, packages } = useFlexPackages();

  if (isLoading) {
    return <Skeleton />;
  }

  // A member with no FlexBenefits purchase must never see a TPA support block.
  if (packages.length === 0) {
    return null;
  }

  const telHref = (phone: string) =>
    helpPhoneUrl.replace("{phone}", phone.replace(/\D/g, ""));

  const [
    administratorBefore,
    administratorAfter,
  ] = flexBenefitsAdministratorText.split("{administrator}");

  return (
    <MDBCard className="mt-3" id="flexbenefits-support">
      <MDBCardBody>
        <h2 className="text-primary mb-1 h2-responsive text-center text-sm-left">
          {flexBenefitsSupportTitle}
        </h2>
        <p className="m-0 mb-3">
          {administratorBefore}
          <strong className="font-weight-bold">
            {flexBenefitsAdministratorName}
          </strong>
          {administratorAfter}
        </p>
        <MDBRow>
          <MDBCol size="12" sm="6" className="mb-3 mb-sm-0">
            <p className="text-uppercase silver-text mb-1">
              <small>{flexBenefitsClaimsLabel}</small>
            </p>
            <a
              id="flexbenefits-claims-phone"
              href={telHref(flexBenefitsClaimsPhone)}
            >
              {flexBenefitsClaimsPhone}
            </a>
            <p className="mb-0">
              <a
                id="flexbenefits-claims-email"
                href={helpEmailUrl.replace("{email}", flexBenefitsClaimsEmail)}
              >
                <small>{flexBenefitsClaimsEmail}</small>
              </a>
            </p>
          </MDBCol>
          <MDBCol size="12" sm="6">
            <p className="text-uppercase silver-text mb-1">
              <small>{flexBenefitsBillingLabel}</small>
            </p>
            <a
              id="flexbenefits-billing-phone"
              href={telHref(flexBenefitsBillingPhone)}
            >
              {flexBenefitsBillingPhone}
            </a>
            <p className="silver-text mb-0">
              <small>{flexBenefitsBillingSubtext}</small>
            </p>
          </MDBCol>
        </MDBRow>
        <MDBRow className="mt-3">
          <MDBCol>
            <FlexBenefitsTpaLinks idPrefix="flexbenefits" />
          </MDBCol>
        </MDBRow>
      </MDBCardBody>
    </MDBCard>
  );
};

export default FlexBenefitsSupport;
